import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import { toast } from "react-toastify";
import ProductionPlan12Month from "./ProductionPlan12Month";

const ProductionPlanReport = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        search: "",
        view: "Monthly Detail",
    });

    const months = [
        { value: 1, label: "January" }, { value: 2, label: "February" },
        { value: 3, label: "March" }, { value: 4, label: "April" },
        { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" },
        { value: 9, label: "September" }, { value: 10, label: "October" },
        { value: 11, label: "November" }, { value: 12, label: "December" },
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    const getReport = async () => {
        const token = getAuthToken();
        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                `${config.API}/senmanager/productionplan`,
                {
                    month: Number(filters.month),
                    year: Number(filters.year),
                    search: filters.search || null,
                    view: filters.view,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success) {
                const result = response.data?.data;
                setRows(Array.isArray(result) ? result : result?.data || []);
            } else {
                setRows([]);
                toast.warning(response.data?.message || "No production plan data found.");
            }
        } catch (error) {
            console.error("Production Plan Error:", error);
            setRows([]);
            toast.error(error.response?.data?.message || "Unable to load production plan.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getReport();
    }, []);

    const getMonthName = () =>
        months.find((m) => Number(m.value) === Number(filters.month))?.label || "";

    const formatNumber = (value) => {
        if (value === null || value === undefined || value === "") return "0";
        const number = Number(value);
        return Number.isNaN(number) ? value : number.toLocaleString("en-IN");
    };

    const handleViewChange = (e) =>
        setFilters({ ...filters, view: e.target.value });

    const resetFilters = () =>
        setFilters({
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            search: "",
            view: "Monthly Detail",
        });

    const headerStyle = {
        color: "#17365d",
        fontWeight: 700,
    };

    const badgeStyle = {
        background: "#e8f1ff",
        color: "#2563eb",
        fontSize: "12px",
        padding: "6px 10px",
    };

    return (
        <div className="container-fluid py-3" style={{ background: "#f5f7fa" }}>

            {/* HEADER */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body py-3 px-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <strong className="fs-6" style={{ color: "#17365d" }}>
                            Production Plan - {getMonthName()} {filters.year}
                        </strong>

                        <div className="d-flex align-items-center gap-2">
                            <label className="fw-semibold mb-0" style={{ color: "#17365d" }}>
                                View
                            </label>

                            <select
                                className="form-select form-select-sm"
                                style={{ width: "155px" }}
                                value={filters.view}
                                onChange={handleViewChange}
                            >
                                <option value="Monthly Detail">Monthly Detail</option>
                                <option value="12 Month Detail">12 Month Detail</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* MONTHLY DETAIL */}
            {filters.view === "Monthly Detail" && (
                <div className="card border-0 shadow-sm">
                    <div className="card-body py-3 px-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">

                            <strong className="fs-6" style={{ color: "#17365d" }}>
                                Detailed Production Plan - {getMonthName()} {filters.year}
                            </strong>

                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <strong style={{ color: "#17365d" }}>Month / Year</strong>

                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: "145px" }}
                                    value={filters.month}
                                    onChange={(e) =>
                                        setFilters({ ...filters, month: e.target.value })
                                    }
                                >
                                    {months.map((month) => (
                                        <option key={month.value} value={month.value}>
                                            {month.label} {filters.year}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: "100px" }}
                                    value={filters.year}
                                    onChange={(e) =>
                                        setFilters({ ...filters, year: e.target.value })
                                    }
                                >
                                    {years.map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>

                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search item or code"
                                    style={{ width: "260px" }}
                                    value={filters.search}
                                    onChange={(e) =>
                                        setFilters({ ...filters, search: e.target.value })
                                    }
                                    onKeyDown={(e) => e.key === "Enter" && getReport()}
                                />

                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary fw-semibold px-3"
                                    onClick={getReport}
                                >
                                    Search
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border fw-semibold"
                                    onClick={resetFilters}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* TABLE */}
                    <div className="table-responsive">
                        <table
                            className="table table-hover align-middle mb-0"
                            style={{ minWidth: "1650px", fontSize: "13px" }}
                        >
                            <thead>
                                <tr style={{
                                    background: "#f1f5f9",
                                    borderBottom: "2px solid #d9e1ea"
                                }}>
                                    {[
                                        ["S.No.", "text-start", "65px"],
                                        ["Item Code", "text-start", ""],
                                        ["Product", "text-start", "360px"],
                                        ["Dispatch", "text-end", ""],
                                        ["Planned Production", "text-end", "130px"],
                                        ["Prev. Production", "text-end", ""],
                                        ["Actual Production", "text-end", ""],
                                        ["IM Machines", "text-center", ""],
                                        ["Operating Schedule", "text-start", "150px"],
                                        ["Mould in Use", "text-start", ""],
                                        ["IM Workmen / Crew Shift", "text-start", "150px"],
                                        ["Monthly IM Capacity", "text-end", "130px"],
                                        ["Closing Stock", "text-end", ""],
                                        ["Shortfall", "text-end", ""],
                                        ["Status", "text-center", ""],
                                    ].map(([title, align, width]) => (
                                        <th
                                            key={title}
                                            className={`py-3 ${align}`}
                                            style={{ ...headerStyle, ...(width ? { minWidth: width } : {}) }}
                                        >
                                            {title}
                                            {title === "Operating Schedule" && (
                                                <div className="text-muted fw-normal" style={{ fontSize: "10px" }}>
                                                    8h / 12h / 2×12h
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="14" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status" />
                                            <div className="text-muted mt-3">
                                                Loading production plan...
                                            </div>
                                        </td>
                                    </tr>
                                ) : rows.length > 0 ? (
                                    rows.map((item, index) => {
                                        const previous_month_pending = Number(
                                            item?.previous_month_pending || 0
                                        )
                                        const produced = Number(item?.qty_produced || 0);
                                        const pending = Number(item?.qty_pending || 0);
                                        const closingStock = Number(
                                            item?.closing_stock ??
                                            item?.stock ??
                                            item?.current_stock ??
                                            0
                                        );
                                        const plannedProduction = pending;
                                        const shortfall = Math.max(0, pending - closingStock);

                                        return (
                                            <tr
                                                key={
                                                    item?.id ||
                                                    `${item?.product?.id}-${item?.po_number}-${item?.plan_month}-${index}`
                                                }
                                                style={{ borderBottom: "1px solid #edf0f3" }}
                                            >
                                                <td className="px-4 text-muted fw-semibold">
                                                    {index + 1}
                                                </td>

                                                <td>
                                                    <span className="fw-bold text-dark" style={{
                                                        fontSize: "14px",
                                                        letterSpacing: "0.3px"
                                                    }}>
                                                        {item?.product?.product_code || "-"}
                                                    </span>
                                                </td>

                                                <td className="fw-semibold text-dark" style={{
                                                    maxWidth: "360px",
                                                    whiteSpace: "normal",
                                                    lineHeight: "1.4"
                                                }}>
                                                    {item?.product?.name || "-"}
                                                </td>

                                                <td className="text-end">
                                                    <span className="fw-semibold text-dark">
                                                        {formatNumber(
                                                            item?.qty_dispatched ??
                                                            item?.stock ??
                                                            item?.dispatch_qty ??
                                                            0
                                                        )}
                                                    </span>
                                                </td>

                                                <td className="text-end">
                                                    <span className="fw-bold" style={{
                                                        color: "#17365d",
                                                        fontSize: "14px"
                                                    }}>
                                                        {formatNumber(plannedProduction)}
                                                    </span>

                                                    {item?.plan_type && (
                                                        <div className="mt-1">
                                                            <span
                                                                className="badge rounded-pill"
                                                                style={{
                                                                    background: "#fff3cd",
                                                                    color: "#946200",
                                                                    fontSize: "10px",
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                {item.plan_type}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <span className="fw-bold" style={{
                                                        color: "#17365d",
                                                        fontSize: "14px"
                                                    }}>
                                                        {formatNumber(previous_month_pending)}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <span className="fw-bold" style={{
                                                        color: "#17365d",
                                                        fontSize: "14px"
                                                    }}>
                                                        {formatNumber(produced)}
                                                    </span>
                                                </td>

                                                <td className="text-center">
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={badgeStyle}
                                                    >
                                                        {formatNumber(item?.machine_count ?? 0)}
                                                    </span>
                                                </td>

                                                <td className="text-center">
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={badgeStyle}
                                                    >
                                                        {item?.shift ?? "-"}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span className="text-dark">
                                                        {item?.die_name ??
                                                            item?.mould ??
                                                            item?.mold_in_use ??
                                                            "-"}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span className="text-dark">
                                                        {item?.user_count ??
                                                            item?.workmen ??
                                                            item?.crew_shift ??
                                                            item?.im_workmen_crew ??
                                                            "-"}
                                                    </span>
                                                </td>

                                                <td className="text-end">
                                                    <span className="fw-semibold text-dark">
                                                        {formatNumber(
                                                            Number(item?.max_cycle_per_month || 0) *
                                                            Number(
                                                                String(item?.die_type || "")
                                                                    .replace(/\D/g, "") || 0
                                                            )
                                                        )}
                                                    </span>
                                                </td>

                                                <td className="text-end">
                                                    <span className="fw-bold" style={{ color: "#198754" }}>
                                                        {formatNumber(closingStock)}
                                                    </span>
                                                </td>

                                                <td className="text-end">
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={{
                                                            background: shortfall > 0 ? "#fde8e8" : "#eef0f2",
                                                            color: shortfall > 0 ? "#dc3545" : "#6c757d",
                                                            fontSize: "12px",
                                                            padding: "6px 10px"
                                                        }}
                                                    >
                                                        NA
                                                    </span>
                                                </td>

                                                <td className="text-center">
                                                    {produced === 0 ? (
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: "#eef0f2",
                                                                color: "#6c757d",
                                                                padding: "7px 12px",
                                                                fontSize: "11px"
                                                            }}
                                                        >
                                                            Not Started
                                                        </span>
                                                    ) : pending === 0 ? (
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: "#e8f7ee",
                                                                color: "#198754",
                                                                padding: "7px 12px",
                                                                fontSize: "11px"
                                                            }}
                                                        >
                                                            Completed
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: "#fff3cd",
                                                                color: "#946200",
                                                                padding: "7px 12px",
                                                                fontSize: "11px"
                                                            }}
                                                        >
                                                            In Production
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="14" className="text-center text-muted py-5">
                                            <div className="fw-semibold mb-1" style={{ fontSize: "14px" }}>
                                                No production plan data
                                            </div>
                                            <small>Try changing the selected filters.</small>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* 12 MONTH DETAIL */}
            {filters.view === "12 Month Detail" && (
                <ProductionPlan12Month
                    month={filters.month}
                    year={filters.year}
                    search={filters.search}
                />
            )}
        </div>
    );
};

export default ProductionPlanReport;